export const presets = Object.freeze({
  low: { maxPixelRatio: 1, fps: 30 },
  medium: { maxPixelRatio: 1.5, fps: 45 },
  high: { maxPixelRatio: 2, fps: 60 },
});
export const defaults = Object.freeze({
  mouseControls: true,
  touchControls: true,
  gyroControls: false,
  scrollControls: false,
  parallax: 0,
  clickControls: false,
  interactionStrength: 1,
  smoothing: 0.08,
  sensitivity: 1,
  backgroundColor: 0x08121f,
  backgroundAlpha: 1,
  placement: "behind",
  zIndex: null,
  pointerEvents: "none",
  quality: "auto",
  maxPixelRatio: 2,
  fps: 60,
  speed: 1,
  intensity: 1,
  pauseOffscreen: true,
  reducedMotion: "static",
  mobile: true,
  autoCleanup: true,
  lazy: false,
  seed: 1,
  scale: 1,
  scaleMobile: 1,
});
export const limits = {
  maxPixelRatio: [0.5, 3],
  fps: [1, 120],
  speed: [0, 10],
  intensity: [0, 10],
  backgroundAlpha: [0, 1],
  smoothing: [0.001, 1],
  interactionStrength: [0, 10],
  sensitivity: [0, 10],
  parallax: [-2, 2],
  scale: [0.5, 16],
  scaleMobile: [0.5, 16],
  quantity: [1, 5],
  points: [1, 20],
  spacing: [0, 100],
  maxDistance: [1, 100],
  zoom: [0.1, 10],
  size: [0.1, 10],
  waveHeight: [0, 100],
  waveSpeed: [0, 10],
  count: [10, 3000],
  rings: [5, 80],
  chaos: [0, 10],
  seed: [0, 4294967295],
};
export function normalize(input = {}, effectDefaults = {}) {
  if (!input || typeof input !== "object" || Array.isArray(input))
    throw new TypeError("Options must be an object");
  const o = { ...defaults, ...effectDefaults, ...input };
  if (!["auto", "low", "medium", "high"].includes(o.quality))
    throw new RangeError("Unknown quality preset");
  if (presets[o.quality])
    for (const k of ["fps", "maxPixelRatio"])
      if (!(k in input)) o[k] = presets[o.quality][k];
  for (const [key, [min, max]] of Object.entries(limits))
    if (key in o) {
      if (
        typeof o[key] !== "number" ||
        !Number.isFinite(o[key]) ||
        o[key] < min ||
        o[key] > max
      )
        throw new RangeError(`${key} must be ${min}–${max}`);
    }
  for (const k of Object.keys(defaults))
    if (typeof defaults[k] === "boolean" && typeof o[k] !== "boolean")
      throw new TypeError(`${k} must be boolean`);
  for (const k of ["quantity", "points", "count", "rings", "seed"])
    if (k in o && !Number.isInteger(o[k]))
      throw new RangeError(`${k} must be an integer`);
  if (!["behind", "front"].includes(o.placement))
    throw new RangeError("placement must be behind or front");
  if (!["none", "auto"].includes(o.pointerEvents))
    throw new RangeError("pointerEvents must be none or auto");
  if (!["static", "disable", "ignore"].includes(o.reducedMotion))
    throw new RangeError("Invalid reducedMotion");
  if (o.zIndex !== null && !Number.isInteger(o.zIndex))
    throw new TypeError("zIndex must be an integer or null");
  for (const [k, v] of Object.entries(o))
    if (
      /^(?:.*Color|color[0-9]*)$/i.test(k) &&
      !(Number.isInteger(v) && v >= 0 && v <= 0xffffff) &&
      !(typeof v === "string" && /^#[\da-f]{6}$/i.test(v))
    )
      throw new TypeError(`${k} must be a 24-bit number or #rrggbb`);
  if (o.onError !== undefined && typeof o.onError !== "function")
    throw new TypeError("onError must be a function");
  return o;
}
export function resolveTarget(target) {
  const el =
    typeof target === "string" ? document.querySelector(target) : target;
  if (!el || el.nodeType !== 1 || el.ownerDocument !== document)
    throw new TypeError("Target must resolve to an element in this document");
  if (
    [
      "IMG",
      "INPUT",
      "TEXTAREA",
      "SELECT",
      "VIDEO",
      "CANVAS",
      "SVG",
      "TABLE",
      "TR",
      "TBODY",
    ].includes(el.tagName)
  )
    throw new TypeError("Use a containing block element as the effect target");
  return el;
}
export function seeded(seed = 1) {
  let a = seed >>> 0;
  return () => {
    a += 0x6d2b79f5;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
